import {
  CURRENCY_OPTIONS,
  MAX_DECIMAL_PLACES
} from '@databases/api-field-settings/constants';
import { type YValueType } from '@databases/collab/types';
import { setValueValue, type YDatabase } from '@databases/collab/y-database';
import { type YEntry } from '@databases/collab/y-entry';
import { type YField } from '@databases/collab/y-field';
import { NumberSeparator } from '@databases/model/custom-number-format';
import { FieldType, NumberFormat } from '@databases/model/field';

/**
 * This migration maps the current number formats (PERCENT, DOLLAR, EURO, and CUSTOM) to the new number formats (NUMBER, PERCENT, CURRENCY)
 */

const unreachable = (value: never) => {
  throw new Error(`Not implemented: ${value}`);
};

function hasPrefixOrSuffix(field: YField): boolean {
  const prefix = field.customNumberFormat.prefix;
  const suffix = field.customNumberFormat.suffix;
  return prefix !== undefined || suffix !== undefined;
}

function hasNoValue(value: YValueType): boolean {
  return !value || value.value === '' || isNaN(Number(value.value));
}

function formatTextValue(value: YValueType, prefix?: string, suffix?: string): string {
  const currentValue = Number(value.value);
  const formattedNumber = currentValue.toLocaleString();
  if (prefix === undefined && suffix === undefined) {
    return `${formattedNumber}`;
  } else if (prefix === undefined) {
    return `${formattedNumber} ${suffix}`;
  } else if (suffix === undefined) {
    return `${prefix} ${formattedNumber}`;
  } else {
    return `${prefix} ${formattedNumber} ${suffix}`;
  }
}

function updateValueToText(
  entry: YEntry,
  fieldId: string,
  value: YValueType,
  prefix?: string,
  suffix?: string
): void {
  const textValue = formatTextValue(value, prefix, suffix);
  if (textValue !== null) {
    const mutableValue = entry.setValue(fieldId, FieldType.Text);
    setValueValue(mutableValue, {
      type: FieldType.Text,
      value: { plain: textValue }
    });
  }
}

function updateTextFormatAndValues(field: YField, ydb: YDatabase, fieldId: string): void {
  const prefix = field.customNumberFormat?.prefix;
  const suffix = field.customNumberFormat?.suffix;

  if (field.type === FieldType.Number) {
    field.type = FieldType.Text;

    // Update default value
    const defaultValue = ydb.defaultEntry.getValue(fieldId);
    if (!hasNoValue(defaultValue)) {
      updateValueToText(ydb.defaultEntry, fieldId, defaultValue, prefix, suffix);
    }

    // Update entry values
    for (const entryId in ydb.entriesById) {
      const entry = ydb.entriesById[entryId];
      const value = entry.getValue(fieldId);
      if (!hasNoValue(value)) {
        updateValueToText(entry, fieldId, value, prefix, suffix);
      }
    }
  }
}

function updateValueToPercent(entry: YEntry, fieldId: string, value: YValueType): void {
  const currentValue = Number(value.value);

  // Convert percent to decimal
  const converted = parseFloat((currentValue / 100).toFixed(10)).toString();
  const mutableValue = entry.setValue(fieldId, FieldType.Number);
  setValueValue(mutableValue, {
    type: FieldType.Number,
    value: converted
  });
}

function updatePercentFormatAndValues(
  field: YField,
  ydb: YDatabase,
  fieldId: string
): void {
  field.numberFormat = NumberFormat.PERCENT;

  // Update the default value
  const defaultValue = ydb.defaultEntry.getValue(fieldId);
  if (!hasNoValue(defaultValue)) {
    updateValueToPercent(ydb.defaultEntry, fieldId, defaultValue);
  }

  // Update all entry values
  for (const entryId in ydb.entriesById) {
    const entry = ydb.entriesById[entryId];
    const value = entry.getValue(fieldId);
    if (!hasNoValue(value)) {
      updateValueToPercent(entry, fieldId, value);
    }
  }
}

const currencyOptions = new Set(CURRENCY_OPTIONS.map((option) => option.value));

function setCustomFormat(field: YField, ydb: YDatabase, fieldId: string): void {
  const prefix = field.customNumberFormat.prefix;
  const suffix = field.customNumberFormat.suffix;

  if (prefix === undefined && suffix === '%') {
    updatePercentFormatAndValues(field, ydb, fieldId);
  } else if (
    suffix === undefined &&
    prefix !== undefined &&
    currencyOptions.has(prefix)
  ) {
    field.numberFormat = NumberFormat.CURRENCY;
  } else if (suffix === '€' && prefix === undefined) {
    field.numberFormat = NumberFormat.CURRENCY;
    field.customNumberFormat.prefix = suffix;
  } else {
    updateTextFormatAndValues(field, ydb, fieldId);
  }
}

function updateSeparator(field: YField): void {
  const thousand = field.customNumberFormat.thousandsSeparator;
  const decimal = field.customNumberFormat.decimalSeparator;
  if (thousand === NumberSeparator.APOSTROPHE) {
    field.customNumberFormat.thousandsSeparator = NumberSeparator.COMMA;
  }

  if (!thousand && decimal === NumberSeparator.COMMA) {
    field.customNumberFormat.decimalSeparator = NumberSeparator.DOT;
  }
}

function updateDecimalPlaces(field: YField): void {
  const minPlaces = field.customNumberFormat.minFractionDigits;
  const maxPlaces = field.customNumberFormat.maxFractionDigits;

  if (!minPlaces && !maxPlaces) {
    return;
  }

  if (maxPlaces) {
    if (maxPlaces > MAX_DECIMAL_PLACES) {
      field.customNumberFormat.decimalPlaces = MAX_DECIMAL_PLACES;
    } else {
      field.customNumberFormat.decimalPlaces = maxPlaces;
    }
  } else if (minPlaces) {
    if (minPlaces > MAX_DECIMAL_PLACES) {
      field.customNumberFormat.decimalPlaces = MAX_DECIMAL_PLACES;
    } else {
      field.customNumberFormat.decimalPlaces = minPlaces;
    }
  }
}

export const newNumberFormat = {
  version: 1747172136145, // Timestamp of the migration when it's written
  apply(documentId: string, ydb: YDatabase) {
    for (const fieldId in ydb.fieldsById) {
      const field = ydb.fieldsById[fieldId];
      const format = field.numberFormat;
      if (!format) {
        continue;
      }
      ydb.mutate(() => {
        switch (format) {
          case NumberFormat.DOLLAR:
            field.numberFormat = NumberFormat.CURRENCY;
            field.customNumberFormat.prefix = '$';
            break;

          case NumberFormat.EURO:
            field.numberFormat = NumberFormat.CURRENCY;
            field.customNumberFormat.prefix = '€';
            break;

          case NumberFormat.CUSTOM:
            if (hasPrefixOrSuffix(field)) {
              setCustomFormat(field, ydb, fieldId);
            } else {
              field.numberFormat = NumberFormat.NUMBER;
            }
            updateDecimalPlaces(field);
            updateSeparator(field);
            break;

          case NumberFormat.PERCENT:
          case NumberFormat.NUMBER:
          case NumberFormat.CURRENCY:
            break;

          default:
            return unreachable(format as never);
        }
      });
    }
  }
};
